// useShareSheetQueue — head-of-queue accessor for the F14.2 share sheet.
//
// The MeshProvider buffers incoming file-offer envelopes into a FIFO.
// ShareSheetHost (Agent D) mounts this hook to surface one offer at a
// time; accept() / decline() invoke the matching mesh helpers, which both
// emit the response envelope and pop the head.

import { useMemo, useCallback } from 'react';
import { useMesh } from './useMesh';

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   size: number,
 *   mime: string,
 *   fromId: string,
 *   ts: number,
 * }} PendingOffer
 */

/**
 * @returns {{
 *   current: PendingOffer | null,
 *   accept: () => void,
 *   decline: (opts?: { block?: boolean }) => void,
 * }}
 */
export function useShareSheetQueue() {
  const { pendingOffers, acceptOffer, declineOffer } = useMesh();

  const current = useMemo(
    () => (pendingOffers && pendingOffers.length > 0 ? pendingOffers[0] : null),
    [pendingOffers],
  );

  const accept = useCallback(() => {
    if (!current) return;
    acceptOffer(current.id);
  }, [current, acceptOffer]);

  const decline = useCallback(
    (opts) => {
      if (!current) return;
      declineOffer(current.id, opts);
    },
    [current, declineOffer],
  );

  return { current, accept, decline };
}

export default useShareSheetQueue;
